import { useUiConfig } from 'api/uiConfig/uiConfig.api'
import PusherSubscriptions from 'pusher/PusherSubscriptions'
import React, { ReactElement, Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import alertStore from 'store/alert/alert'
import authStore from 'store/auth/auth'
import { LoadingPlaceholderContainer } from 'ui/atoms'
import Alert from 'ui/components/Alert/Alert'
import ErrorFallback from 'ui/components/ErrorFallback/ErrorFallback'
import ImpersonatingBanner from 'ui/components/ImpersonatingBanner/ImpersonatingBanner'
import Navigation from 'ui/components/Navigation/Navigation'
import PrivateRoutes from 'ui/components/PrivateRoutes/PrivateRoutes'
import StageBanner from 'ui/components/StageBanner/StageBanner'
import sentry from 'utils/sentry/sentry'
import { useSnapshot } from 'valtio'
import styles from './MainLayout.module.less'
import SyncSystemTagsWithStore from './SyncSystemTagsWithStore'
import { useActiveSystemRoutes } from './useActiveSystemRoutes'

export default function MainLayout(): ReactElement {
  const routes = useActiveSystemRoutes()
  const alertStoreSnap = useSnapshot(alertStore)

  const { systemId } = useSnapshot(authStore)

  useUiConfig(systemId, `base_config`)

  if (!routes) {
    return <LoadingPlaceholderContainer height="100%" />
  }

  return (
    <div className={styles.MainLayout_Root}>
      <div className={styles.MainLayout_TopBannersContainer}>
        <ImpersonatingBanner inline />
        <StageBanner inline />
      </div>
      <Navigation routes={routes}>
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={(error) => sentry.captureBoundaryException(error, { boundaryName: 'MainLayoutPrivateRoutes' })}
        >
          <Suspense fallback={<LoadingPlaceholderContainer />}>
            <PrivateRoutes routes={routes} />
          </Suspense>
        </ErrorBoundary>
        <Alert
          variant={alertStoreSnap.alert?.variant ? alertStoreSnap.alert.variant : `success`}
          message={alertStoreSnap.alert?.message ? alertStoreSnap.alert.message : ``}
          open={!!alert}
          onClose={alertStoreSnap.alertClear}
          SnackbarProps={alertStoreSnap.alert?.props}
          alertKey={alertStoreSnap.alert?.alertKey}
          action={alertStoreSnap.alert?.action}
          note={alertStoreSnap.alert?.note}
        />
        <PusherSubscriptions />
        <SyncSystemTagsWithStore />
      </Navigation>
    </div>
  )
}
